// Business Actions Component
// Feature: 005-business-administration

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, RotateCcw } from 'lucide-react';
import type { BusinessStatus } from '@/types/businessAdmin';

interface BusinessActionsProps {
  currentStatus: BusinessStatus;
  onApprove: () => Promise<{ success: boolean; error?: string }>;
  onDisable: (reason: string) => Promise<{ success: boolean; error?: string }>;
  onReactivate: () => Promise<{ success: boolean; error?: string }>;
}

export function BusinessActions({
  currentStatus,
  onApprove,
  onDisable,
  onReactivate,
}: BusinessActionsProps) {
  const { t } = useTranslation('businessAdmin');
  const [showConfirm, setShowConfirm] = useState<'approve' | 'disable' | 'reactivate' | null>(
    null
  );
  const [disableReason, setDisableReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApprove = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      const result = await onApprove();
      if (result.success) {
        setShowConfirm(null);
      } else {
        setError(result.error || t('messages.error'));
      }
    } catch (err) {
      setError(t('messages.error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDisable = async () => {
    if (!disableReason.trim()) {
      setError(t('messages.reasonRequired'));
      return;
    }

    setIsProcessing(true);
    setError(null);
    try {
      const result = await onDisable(disableReason);
      if (result.success) {
        setShowConfirm(null);
        setDisableReason('');
      } else {
        setError(result.error || t('messages.error'));
      }
    } catch (err) {
      setError(t('messages.error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReactivate = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      const result = await onReactivate();
      if (result.success) {
        setShowConfirm(null);
      } else {
        setError(result.error || t('messages.error'));
      }
    } catch (err) {
      setError(t('messages.error'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3" role="group" aria-label={t('actions.statusActions')}>
        {currentStatus === 'awaiting_moderation' && (
          <button
            onClick={() => setShowConfirm('approve')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowConfirm('approve');
              }
            }}
            aria-label={t('actions.approveAria')}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
          >
            <CheckCircle size={18} aria-hidden="true" />
            {t('actions.approve')}
          </button>
        )}

        {currentStatus === 'active' && (
          <button
            onClick={() => setShowConfirm('disable')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowConfirm('disable');
              }
            }}
            className="flex items-center gap-2 px-4 py-2 border-2 border-red-600 text-red-600 bg-white rounded-lg hover:bg-red-50 transition font-medium"
          >
            <XCircle size={18} />
            {t('actions.disable')}
          </button>
        )}

        {currentStatus === 'disabled' && (
          <button
            onClick={() => setShowConfirm('reactivate')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowConfirm('reactivate');
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium"
          >
            <RotateCcw size={18} />
            {t('actions.reactivate')}
          </button>
        )}
      </div>

      {/* Confirmation Modals */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setShowConfirm(null);
              setDisableReason('');
              setError(null);
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-neutral-900 mb-4">
              {t(`confirm.${showConfirm}.title`)}
            </h3>

            {showConfirm === 'disable' ? (
              <>
                <p className="text-neutral-700 mb-4">{t('confirm.disable.message')}</p>
                <div className="mb-4">
                  <label
                    htmlFor="disable-reason"
                    className="block text-sm font-medium text-neutral-700 mb-2"
                  >
                    {t('confirm.disable.reasonLabel')}
                  </label>
                  <textarea
                    id="disable-reason"
                    value={disableReason}
                    onChange={(e) => setDisableReason(e.target.value)}
                    placeholder={t('confirm.disable.reasonPlaceholder')}
                    rows={3}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={isProcessing}
                  />
                </div>
              </>
            ) : (
              <p className="text-neutral-700 mb-4">
                {t(`confirm.${showConfirm}.message`)}
              </p>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowConfirm(null);
                  setDisableReason('');
                  setError(null);
                }}
                disabled={isProcessing}
                className="px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition disabled:opacity-50"
              >
                {t(`confirm.${showConfirm}.cancel`)}
              </button>
              <button
                onClick={() => {
                  if (showConfirm === 'approve') handleApprove();
                  else if (showConfirm === 'disable') handleDisable();
                  else if (showConfirm === 'reactivate') handleReactivate();
                }}
                disabled={isProcessing}
                className={`px-4 py-2 text-white rounded-lg transition font-medium disabled:opacity-50 flex items-center gap-2 ${
                  showConfirm === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : showConfirm === 'disable'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                {isProcessing && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                {t(`confirm.${showConfirm}.confirm`)}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
